import React from "react";

import * as Styled from "./styles";
import { IFindDoctor } from "shared/types";

import { PiMapPinLine } from "react-icons/pi";
import { formatPhone } from "shared/helper";
import { FaWaze } from "react-icons/fa";
import { FiMapPin } from "react-icons/fi";
import { FaPhoneAlt } from "react-icons/fa";
import { FaBriefcaseMedical } from "react-icons/fa";
import { AiOutlineGlobal } from "react-icons/ai";
import { FaWhatsapp } from "react-icons/fa";

interface IProps {
  data: IFindDoctor;
  impression: boolean;
}

const Card: React.FC<IProps> = ({ data, impression }) => {
  const query = encodeURIComponent(
    `${data.END_LOGRADOURO}, ${data.END_NUMERO} ${data.END_CIDADE} ${data.END_ESTADO} Brasil`,
  );

  const formatDistance = (distance: number) => {
    if (distance === 0) {
      return "0KM";
    }

    if (distance < 1) {
      const distanceStr = distance.toString();
      const decimalPart = distanceStr.split(".")[1] || "000";
      return `${decimalPart.slice(0, 3)}M`;
    } else {
      return `${Math.floor(distance).toString()}KM`;
    }
  };

  const separateCNPJ = (d: string) => {
    const parts = d.split(" - ");

    if (parts.length === 3) {
      const cnpj = parts[0].trim();
      const name = parts[1].trim().toUpperCase();
      const name2 = parts[2].trim().toUpperCase();

      return `${name} - ${name2}: ${cnpj}`;
    }

    if (parts.length === 2) {
      const cnpj = parts[0].trim();
      const name = parts[1].trim().toUpperCase();

      return `${name}: ${cnpj}`;
    } else {
      return d;
    }
  };

  const RedePropria = data.CAT_NOME === "Rede Própria" && (
    <Styled.BadgeRedePropria>
      <Styled.BadgeIconSlot>
        <img src="rede_propria_icon.svg" alt="" width={20} height={20} />
      </Styled.BadgeIconSlot>
      Rede Própria
    </Styled.BadgeRedePropria>
  );

  const RedeReferenciada = Number(data.REDE_REFERENCIADA) === 1 && (
    <Styled.BadgeRedeReferenciada>
      <Styled.BadgeIconSlot>
        <img src="rede_referenciada_icon.svg" alt="" width={20} height={20} />
      </Styled.BadgeIconSlot>
      Rede Referenciada
    </Styled.BadgeRedeReferenciada>
  );

  return (
    <Styled.Container>
      <Styled.Header impression={impression}>
        <h5 style={{ textTransform: "uppercase" }}>
          {data.ESP_NOME ? data.ESP_NOME : ""}
          {data.DISTANCE ? (
            <Styled.DistanceMobile>
              <b>Distância:</b>{" "}
              {data.DISTANCE
                ? `${formatDistance(data.DISTANCE)}`
                : "Não Informado"}
            </Styled.DistanceMobile>
          ) : (
            ""
          )}
        </h5>

        <Styled.Access>
          {data.DISTANCE === 0 ? (
            <Styled.Distance>No local</Styled.Distance>
          ) : (
            <Styled.Distance>
              <b>Distância:</b>{" "}
              {data.DISTANCE
                ? `${formatDistance(data.DISTANCE)}`
                : "Não Informado"}
            </Styled.Distance>
          )}
          <Styled.ButttonHeader
            href={`https://www.google.com/maps/search/?api=1&query=${query}`}
            target="_blank"
            rel="noreferrer"
          >
            <PiMapPinLine size="20px" />
            <p>Ver no Mapa</p>
          </Styled.ButttonHeader>
          <Styled.ButttonHeaderWaze
            href={`waze://?ll=${data.END_LATITUDE},${data.END_LONGITUDE}&navigate=yes`}
            target="_blank"
            rel="noreferrer"
          >
            <FaWaze size="20px" />
            <p>Ver no Waze</p>
          </Styled.ButttonHeaderWaze>
        </Styled.Access>
      </Styled.Header>
      <Styled.Body impression={impression}>
        <Styled.Left>
          <Styled.Line>
            <Styled.TitleName>
              {data.PRE_FANTASIA ? data.PRE_FANTASIA : "Não Informado"}{" "}
              {RedePropria}
              {RedeReferenciada}
            </Styled.TitleName>
          </Styled.Line>
          {data.QUA_SIGLA && (
            <Styled.Line>
              <Styled.LineSiglas>
                {data.QUA_SIGLA.split(",").map((sigla, siglaIndex) => (
                  <img
                    key={`sigla${siglaIndex}`}
                    src={`quali_icones/${sigla}.png`}
                    alt={sigla}
                    width={20}
                    height={20}
                  />
                ))}
              </Styled.LineSiglas>
            </Styled.Line>
          )}

          <Styled.Line>
            <Styled.SocialName>{separateCNPJ(data.PRE_NOME)}</Styled.SocialName>
          </Styled.Line>

          <Styled.Line>
            <Styled.Title>
              <FiMapPin size={20} color="#006f5e" />
            </Styled.Title>
            <Styled.DescTell
              href={`https://www.google.com/maps/search/?api=1&query=${query}`}
              target="_blank"
              rel="noreferrer"
            >
              {/*data.END_LOGRADOURO
                ? `${data.END_LOGRADOURO}, ${data.END_NUMERO} ${
                    data.END_COMPLEMENTO ? data.END_COMPLEMENTO : ""
                  }, ${data.END_BAIRRO}, ${data.END_CIDADE} - ${
                    data.END_ESTADO
                  }, ${data.END_CEP}`
                : "Não Informado"*/}

              <Styled.Line>
                <Styled.Title>Logradouro:</Styled.Title>
                <Styled.Description>
                  {data.END_LOGRADOURO}, {data.END_NUMERO}
                  {data.END_COMPLEMENTO ? `, ${data.END_COMPLEMENTO}` : ""}
                </Styled.Description>
              </Styled.Line>

              <Styled.Line>
                <Styled.Title>Bairro:</Styled.Title>
                <Styled.Description>{data.END_BAIRRO}</Styled.Description>
              </Styled.Line>

              <Styled.Line>
                <Styled.Title>Cidade/UF:</Styled.Title>
                <Styled.Description>
                  {data.END_CIDADE}/{data.END_ESTADO}
                </Styled.Description>
              </Styled.Line>

              <Styled.Line>
                <Styled.Title>Cep:</Styled.Title>
                <Styled.Description>{data.END_CEP}</Styled.Description>
              </Styled.Line>
            </Styled.DescTell>
          </Styled.Line>

          {data.END_TELEFONE_01 && data.END_TELEFONE_02 && (
            <Styled.Line>
              <Styled.Title>
                <FaPhoneAlt size={20} color="#006f5e" />
              </Styled.Title>
              <Styled.DescTell href={`tel:${data.END_TELEFONE_01}`}>
                {formatPhone(data.END_TELEFONE_01)}
              </Styled.DescTell>
              &nbsp;-&nbsp;
              <Styled.DescTell href={`tel:${data.END_TELEFONE_02}`}>
                {formatPhone(data.END_TELEFONE_02)}
              </Styled.DescTell>
            </Styled.Line>
          )}

          {!data.END_TELEFONE_01 && data.END_TELEFONE_02 && (
            <Styled.Line>
              <Styled.Title>
                <FaPhoneAlt size={20} color="#006f5e" />
              </Styled.Title>
              <Styled.DescTell href={`tel:${data.END_TELEFONE_02}`}>
                {formatPhone(data.END_TELEFONE_02)}
              </Styled.DescTell>
            </Styled.Line>
          )}

          {data.END_TELEFONE_01 && !data.END_TELEFONE_02 && (
            <Styled.Line>
              <Styled.Title>
                <FaPhoneAlt size={20} color="#006f5e" />
              </Styled.Title>
              <Styled.DescTell href={`tel:${data.END_TELEFONE_01}`}>
                {formatPhone(data.END_TELEFONE_01)}
              </Styled.DescTell>
            </Styled.Line>
          )}

          {data.END_WHATSAPP_01.toString().length > 8 &&
            data.END_WHATSAPP_02.toString().length > 8 && (
              <Styled.Line>
                <Styled.Title>
                  <FaWhatsapp size={20} color="#006f5e" />
                </Styled.Title>
                <Styled.DescTell href={`https://wa.me/${data.END_WHATSAPP_01}`}>
                  {formatPhone(data.END_WHATSAPP_01.toString())}
                </Styled.DescTell>
                &nbsp;-&nbsp;
                <Styled.DescTell href={`https://wa.me/${data.END_WHATSAPP_02}`}>
                  {formatPhone(data.END_WHATSAPP_02.toString())}
                </Styled.DescTell>
              </Styled.Line>
            )}

          {data.END_WHATSAPP_01.toString().length > 8 &&
            data.END_WHATSAPP_02 === 0 && (
              <Styled.Line>
                <Styled.Title>
                  <FaWhatsapp size={20} color="#006f5e" />
                </Styled.Title>
                <Styled.DescTell href={`https://wa.me/${data.END_WHATSAPP_01}`}>
                  {formatPhone(data.END_WHATSAPP_01.toString())}
                </Styled.DescTell>
              </Styled.Line>
            )}

          {data.END_WHATSAPP_01 === 0 &&
            data.END_WHATSAPP_02.toString().length > 8 && (
              <Styled.Line>
                <Styled.Title>
                  <FaWhatsapp size={20} color="#006f5e" />
                </Styled.Title>
                <Styled.DescTell href={`https://wa.me/${data.END_WHATSAPP_02}`}>
                  {formatPhone(data.END_WHATSAPP_02.toString())}
                </Styled.DescTell>
              </Styled.Line>
            )}

          <Styled.Line>
            <Styled.Title>
              <FaBriefcaseMedical size={20} color="#006f5e" />
            </Styled.Title>
            <Styled.Description>
              {data.TIP_NOME ? data.TIP_NOME.toUpperCase() : "Não Informado"}
            </Styled.Description>
          </Styled.Line>

          {data.PRE_SITE ? (
            <Styled.Line>
              <Styled.Title>
                <AiOutlineGlobal size={20} color="#006f5e" />
              </Styled.Title>

              <Styled.DescTell href={data.PRE_SITE} target="_blank">
                {data.PRE_SITE}
              </Styled.DescTell>
            </Styled.Line>
          ) : (
            ""
          )}

          {/* <Styled.Line>
            <Styled.Title>Especialidade(s):</Styled.Title>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {data.RELATIONAL_SPECIALIST.map((item, index) => (
                <p key={index}>
                  {index === data.RELATIONAL_SPECIALIST.length - 1
                    ? item.ESP_NOME
                    : item.ESP_NOME + ","}
                  &nbsp;
                </p>
              ))}
            </div>
          </Styled.Line> */}
        </Styled.Left>
      </Styled.Body>
      {data.RELATIONAL_SPECIALIST_PLANS.map((splan, index) => (
        <Styled.BarPlan impression={impression}>
          <p>
            <span>Especialidade(s): </span>
            {splan.ESPECIALIDADE}
          </p>
          <p>
            <span>Planos atendidos: </span> {splan.PLANOS_ATENDIDOS}
          </p>

          {/* {data.RELATIONAL_INSURANCE.map((item, index) => (
            <>
              <Chip
                label={item.PLN_SIGLA}
                key={index}
                sx={{ backgroundColor: "transparent", color: "black" }}
              />
              <Divider
                orientation="vertical"
                sx={{ backgroundColor: "black", height: "28px" }}
              />
            </>
          ))} */}
        </Styled.BarPlan>
      ))}
    </Styled.Container>
  );
};

export { Card };
